import React, { useEffect, useMemo } from "react";
import {
  Typography,
  Space,
  Button,
  App,
  Alert,
  theme,
  Row,
  Col,
} from "antd";
import {
  KeyOutlined,
  SafetyCertificateOutlined,
  DesktopOutlined,
  PoweroffOutlined,
} from "@ant-design/icons";
import { useLanguage } from "../providers/LanguageProvider";
import useSessions from "src/hooks/useSessions";
import ChangePasswordForm from "./security/ChangePasswordForm";
import PasswordChangesList from "src/components/passwords/PasswordChangesList";
import useChangePasswordHistory from "src/hooks/useChangePasswordHistory";

const { Title, Text } = Typography;

export default function SecurityPassword() {
  const { message } = App.useApp();
  const { t } = useLanguage();
  const { token } = theme.useToken();

  const session = useSessions() as any;
  const changePasswordHistory = useChangePasswordHistory();

  const fetchSessions: (() => Promise<any>) | undefined =
    session?.fetchSessions ?? session?.reload;
  const logoutAll: (() => Promise<any>) | undefined =
    session?.logoutAll ?? session?.revokeAll;

  useEffect(() => {
    fetchSessions?.();
    changePasswordHistory.fetchData?.();
  }, []);

  const sessionsArray: any[] =
    session?.items ?? session?.data ?? session?.sessions ?? [];
  const activeSessionCount = Array.isArray(sessionsArray) ? sessionsArray.length : 0;

  const lastPasswordChange = useMemo(() => {
    const list: any[] = changePasswordHistory?.data ?? [];
    if (!Array.isArray(list) || list.length === 0) return null;

    const getDate = (it: any) =>
      new Date(
        it?.changedAt ??
          it?.ChangedAt ??
          it?.updatedAt ??
          it?.UpdatedAt ??
          it?.createdAt ??
          it?.CreatedAt ??
          it?.date ??
          it?.Date ??
          it?.timestamp ??
          it?.at ??
          0
      ).getTime();

    const sorted = [...list]
      .map((x) => ({ x, ts: getDate(x) }))
      .filter((y) => Number.isFinite(y.ts))
      .sort((a, b) => b.ts - a.ts);

    return sorted[0]?.ts ? new Date(sorted[0].ts) : null;
  }, [changePasswordHistory?.data]);

  const daysSinceChange =
    lastPasswordChange ? Math.floor((Date.now() - lastPasswordChange.getTime()) / 86400000) : null;
  const shouldWarnRotate = daysSinceChange != null && daysSinceChange >= 90;

  const handleLogoutAll = async () => {
    if (!logoutAll) return;
    try {
      await logoutAll();
      message.success(t("ALL_SESSIONS_CLOSED"));
      fetchSessions?.();
    } catch {
      message.error(t("ERROR_GENERIC"));
    }
  };

  const heroStyle: React.CSSProperties = {
    borderRadius: 16,
    padding: 16,
    border: `1px solid ${token.colorSplit}`,
    background: token.colorBgContainer,
  };
  const pillStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    border: `1px solid ${token.colorSplit}`,
    background: token.colorBgContainer,
    whiteSpace: "nowrap",
  };
  const metricBox: React.CSSProperties = {
    borderRadius: 12,
    border: `1px solid ${token.colorSplit}`,
    padding: 12,
    background: token.colorBgContainer,
    height: "100%",
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* HERO / HEADER */}
      <div style={heroStyle}>
        <Space direction="vertical" size={10} style={{ width: "100%" }}>
          <Space wrap size={8}>
            <span style={pillStyle}>
              <KeyOutlined />
              {t("SECURITY_PASSWORD")}
            </span>
            <span style={pillStyle}>
              <SafetyCertificateOutlined />
              {t("BEST_PRACTICES")}
            </span>
          </Space>

          <Title level={3} style={{ margin: 0 }}>
            {t("PASSWORD_PAGE_TITLE")}
          </Title>

          <Text type="secondary">
            {t("PASSWORD_PAGE_DESC")}
          </Text>

          <Row gutter={[12, 12]} style={{ marginTop: 4 }}>
            <Col xs={24} md={8}>
              <div style={metricBox}>
                <Space align="center">
                  <DesktopOutlined />
                  <Text strong>{t("ACTIVE_SESSIONS")}</Text>
                </Space>
                <div style={{ marginTop: 6 }}>
                  <Title level={4} style={{ margin: 0 }}>
                    {activeSessionCount}
                  </Title>
                  <Text type="secondary">{t("ACTIVE_SESSIONS_HINT")}</Text>
                </div>
              </div>
            </Col>

            <Col xs={24} md={8}>
              <div style={metricBox}>
                <Space align="center">
                  <KeyOutlined />
                  <Text strong>{t("LAST_CHANGE")}</Text>
                </Space>
                <div style={{ marginTop: 6 }}>
                  <Title level={4} style={{ margin: 0 }}>
                    {lastPasswordChange ? lastPasswordChange.toLocaleString() : t("NEVER")}
                  </Title>
                  <Text type="secondary">
                    {daysSinceChange != null
                      ? t("DAYS_AGO", { count: daysSinceChange })
                      : t("ROTATE_OFTEN")}
                  </Text>
                </div>
              </div>
            </Col>

            <Col xs={24} md={8}>
              <div style={metricBox}>
                <Space align="center">
                  <SafetyCertificateOutlined />
                  <Text strong>{t("TIPS")}</Text>
                </Space>
                <div style={{ marginTop: 6 }}>
                  <Text>{t("TIP_TEXT")}</Text>
                </div>
              </div>
            </Col>
          </Row>

          <Space wrap style={{ marginTop: 8 }}>
            <Button
              type="primary"
              icon={<KeyOutlined />}
              onClick={() =>
                document
                  .getElementById("change-password-form")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
            >
              {t("CHANGE_PASSWORD")}
            </Button>

            {logoutAll && (
              <Button icon={<PoweroffOutlined />} onClick={handleLogoutAll}>
                {t("LOGOUT_ALL")}
              </Button>
            )}
          </Space>

          {shouldWarnRotate && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message={t("ROTATE_WARNING_TITLE")}
              description={t("ROTATE_WARNING_DESC")}
            />
          )}
        </Space>
      </div>

      {/* FORM & LIST */}
      <div id="change-password-form">
        <ChangePasswordForm />
      </div>

      <PasswordChangesList
        loading={changePasswordHistory.loading}
        items={changePasswordHistory.data}
      />
    </div>
  );
}
